import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, CreditCard, UserPlus, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Licenses = ({ orgId }) => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [purchaseData, setPurchaseData] = useState({
    quantity: 1,
    email: '',
    name: '',
    auto_assign: false
  });

  useEffect(() => {
    loadLicenses();
  }, [orgId]);

  const loadLicenses = async () => {
    setLoading(true);
    try {
      // Cargar licencias del usuario actual (no de organización)
      const res = await axios.get(`${API}/licenses`);
      setLicenses(res.data.licenses || []);
    } catch (error) {
      setLicenses([]);
    }
    setLoading(false);
  };

  const handlePurchaseLicense = async () => {
    try {
      // Usar endpoint simplificado SIN organización
      const res = await axios.post(`${API}/licenses/purchase`, {
        quantity: purchaseData.quantity,
        user_email: purchaseData.auto_assign ? purchaseData.email : null,
        user_name: purchaseData.auto_assign ? purchaseData.name : null
      });

      // Redirigir a Stripe Checkout
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e).join(', '));
      } else {
        toast.error(errorMsg || 'Error al procesar el pago');
      }
    }
  };

  const LICENSE_PRICE = 24.99; // €24.99 por licencia (mismo que Premium)

  // Calcular descuento
  const getDiscount = (qty) => {
    if (qty >= 50) return 50;
    if (qty >= 20) return 20;
    if (qty >= 10) return 10;
    return 0;
  };

  const calculateTotal = () => {
    const discount = getDiscount(purchaseData.quantity);
    const pricePerUnit = LICENSE_PRICE * (1 - discount / 100);
    const total = pricePerUnit * purchaseData.quantity;
    return total.toFixed(2);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-black text-yellow-500 flex items-center gap-3">
          <Crown size={28} />
          GESTIÓN DE LICENCIAS
        </h2>
        <button
          onClick={() => setShowPurchaseForm(!showPurchaseForm)}
          className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-6 py-3 font-bold flex items-center gap-2 hover:scale-105 transition border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
        >
          <CreditCard size={20} />
          Comprar Licencias
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-4 border-yellow-500 p-6">
        <h3 className="text-yellow-400 font-bold text-lg mb-2 flex items-center gap-2">
          <Crown size={20} /> ¿Qué son las Licencias Premium?
        </h3>
        <ul className="text-white space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <Check className="text-green-400 flex-shrink-0 mt-0.5" size={16} />
            <span>Cada licencia cuesta <strong>€{LICENSE_PRICE}</strong> y otorga acceso Premium a un usuario</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="text-green-400 flex-shrink-0 mt-0.5" size={16} />
            <span>Puedes asignar licencias automáticamente a nuevos usuarios al comprarlas</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="text-green-400 flex-shrink-0 mt-0.5" size={16} />
            <span>Los usuarios con licencia Premium tienen acceso completo al CRM y todas las funcionalidades</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="text-green-400 flex-shrink-0 mt-0.5" size={16} />
            <span>Puedes comprar múltiples licencias a la vez para tu equipo</span>
          </li>
        </ul>
      </div>

      {/* Purchase Form */}
      {showPurchaseForm && (
        <div className="bg-gray-900 border-4 border-yellow-500 p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <p className="text-white font-bold mb-4 flex items-center gap-2 text-lg">
            <CreditCard size={20} /> COMPRAR LICENCIAS PREMIUM
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="text-gray-300 text-sm font-bold mb-2 block">Cantidad de Licencias</label>
              <input
                type="number"
                min="1"
                max="100"
                value={purchaseData.quantity}
                onChange={(e) => setPurchaseData({...purchaseData, quantity: parseInt(e.target.value) || 1})}
                className="w-full bg-black border-2 border-yellow-500 p-3 text-white font-bold text-2xl text-center"
              />
              <div className="mt-3 space-y-2">
                {getDiscount(purchaseData.quantity) > 0 && (
                  <p className="text-green-400 text-sm font-bold flex items-center gap-2">
                    🎉 ¡{getDiscount(purchaseData.quantity)}% de descuento aplicado!
                  </p>
                )}
                <p className="text-gray-400 text-sm">
                  Precio por licencia: <span className={`font-bold ${getDiscount(purchaseData.quantity) > 0 ? 'text-green-400' : 'text-yellow-400'}`}>€{(LICENSE_PRICE * (1 - getDiscount(purchaseData.quantity) / 100)).toFixed(2)}</span>
                  {getDiscount(purchaseData.quantity) > 0 && <span className="line-through text-gray-600 ml-2">€{LICENSE_PRICE}</span>}
                </p>
                <p className="text-gray-400 text-sm">
                  Total: <span className="text-yellow-400 font-bold text-xl">€{calculateTotal()}</span>
                </p>
              </div>
            </div>

            <div className="bg-gray-800 border-2 border-gray-700 p-4 rounded">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={purchaseData.auto_assign}
                  onChange={(e) => setPurchaseData({...purchaseData, auto_assign: e.target.checked})}
                  className="w-5 h-5"
                />
                <span className="text-white font-bold">Asignar automáticamente a un usuario nuevo</span>
              </label>
            </div>

            {purchaseData.auto_assign && (
              <div className="bg-gray-800 border-2 border-purple-500 p-4 space-y-3">
                <div>
                  <label className="text-gray-300 text-sm font-bold mb-2 block">Nombre del Usuario</label>
                  <input
                    type="text"
                    value={purchaseData.name}
                    onChange={(e) => setPurchaseData({...purchaseData, name: e.target.value})}
                    placeholder="Juan Pérez"
                    className="w-full bg-black border-2 border-purple-500 p-3 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm font-bold mb-2 block">Email del Usuario</label>
                  <input
                    type="email"
                    value={purchaseData.email}
                    onChange={(e) => setPurchaseData({...purchaseData, email: e.target.value})}
                    placeholder="usuario@ejemplo.com"
                    className="w-full bg-black border-2 border-purple-500 p-3 text-white"
                  />
                </div>
                <p className="text-gray-400 text-sm flex items-start gap-2">
                  <UserPlus size={16} className="flex-shrink-0 mt-0.5" />
                  Se creará una cuenta automáticamente y se enviará un email con las credenciales
                </p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={handlePurchaseLicense}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-black py-4 font-bold text-lg hover:scale-105 transition border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
              >
                💳 PAGAR €{calculateTotal()}
              </button>
              <button
                onClick={() => setShowPurchaseForm(false)}
                className="px-6 py-4 bg-gray-700 text-white font-bold hover:bg-gray-600 border-2 border-black"
              >
                CANCELAR
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Licenses List */}
      <div>
        <h3 className="text-white font-bold text-lg mb-4">LICENCIAS ACTIVAS</h3>
        {loading ? (
          <p className="text-white text-center py-8">Cargando licencias...</p>
        ) : licenses.length === 0 ? (
          <div className="bg-gray-900 border-4 border-dashed border-gray-700 p-12 text-center">
            <Crown size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg">No hay licencias activas</p>
            <p className="text-gray-500 text-sm mt-2">Compra licencias para dar acceso Premium a tu equipo</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {licenses.map((license, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500 p-4 flex justify-between items-center"
              >
                <div>
                  <p className="text-white font-bold">{license.user_name || license.user_email}</p>
                  <p className="text-gray-400 text-sm">{license.user_email}</p>
                </div>
                <div className="text-right">
                  <span className="bg-green-500 text-white px-3 py-1 text-xs font-bold border-2 border-black">
                    ACTIVA
                  </span>
                  <p className="text-gray-500 text-xs mt-1">
                    Desde: {new Date(license.purchased_at).toLocaleDateString('es-ES')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
